import {
  DEFAULT_API_FALLBACK,
  DEFAULT_BACKEND_PORT,
  DEFAULT_DEV_VITE_PORTS
} from "./constants";

export function normalizeApiBase(raw: string): string {
  return raw.trim().replace(/\/+$/, "");
}

export function isLoopbackHost(hostname: string): boolean {
  const h = hostname.trim().toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "::1";
}

/**
 * 局域网访问时，将 env 里的 127.0.0.1/localhost 改写为当前页 hostname。
 */
export function rewriteLoopbackApiBaseForLanPage(apiBase: string): string {
  if (typeof window === "undefined") return apiBase;
  const pageHost = window.location.hostname.trim().toLowerCase();
  if (isLoopbackHost(pageHost)) return apiBase;
  try {
    const trimmed = normalizeApiBase(apiBase);
    const url = new URL(trimmed);
    if (!isLoopbackHost(url.hostname)) return apiBase;
    url.hostname = window.location.hostname;
    return normalizeApiBase(url.toString());
  } catch {
    return apiBase;
  }
}

export type ReadApiBaseOptions = {
  /** 如 Vite：`() => import.meta.env.VITE_API_BASE` */
  getEnvApiBase?: () => string | undefined;
  /** 当前端 dev 端口列表，命中则回退到 backendPort */
  devVitePorts?: readonly string[];
  backendPort?: string;
  fallback?: string;
};

/**
 * 解析 API 根路径（web / admin 共用逻辑）。
 */
export function readApiBase(options: ReadApiBaseOptions = {}): string {
  const devPorts = new Set(
    options.devVitePorts ?? DEFAULT_DEV_VITE_PORTS
  );
  const backendPort = options.backendPort ?? DEFAULT_BACKEND_PORT;
  const fallback = options.fallback ?? DEFAULT_API_FALLBACK;

  const fromEnv = options.getEnvApiBase?.();
  if (typeof fromEnv === "string") {
    const n = normalizeApiBase(fromEnv);
    if (n.length > 0) {
      return normalizeApiBase(rewriteLoopbackApiBaseForLanPage(n));
    }
  }

  try {
    if (typeof window !== "undefined" && window.location?.origin) {
      const { origin, hostname, protocol, port } = window.location;
      if (isLoopbackHost(hostname)) {
        if (port && port !== backendPort) {
          return normalizeApiBase(
            `${protocol}//${hostname}:${backendPort}/api`
          );
        }
        return normalizeApiBase(`${origin.replace(/\/+$/, "")}/api`);
      }
      if (port && devPorts.has(port)) {
        return normalizeApiBase(
          `${protocol}//${hostname}:${backendPort}/api`
        );
      }
      if (origin && origin !== "null") {
        return normalizeApiBase(`${origin.replace(/\/+$/, "")}/api`);
      }
    }
  } catch {
    /* ignore */
  }

  return fallback;
}
